/** Instalação do app web (PWA) — desktop, Android e iOS. */

export const PWA_DISMISS_KEY = 'aurax_pwa_install_dismiss_until';
export const PWA_NEVER_KEY = 'aurax_pwa_install_never';
export const PWA_DISMISS_MS = 3 * 24 * 60 * 60 * 1000; // 3 dias

let deferredPrompt = null;
let captureInstalled = false;
let manifestObjectUrl = null;
let currentPwaIconUrl = '';

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches) return true;
  } catch {
    /* ignore */
  }
  return Boolean(window.navigator?.standalone);
}

/** @returns {'ios'|'android'|'windows'|'mac'|'other'} */
export function getInstallPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const iPadOs =
    navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/i.test(ua) || iPadOs) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  return 'other';
}

export function canUseNativeInstallPrompt() {
  return Boolean(deferredPrompt);
}

export function getDeferredInstallPrompt() {
  return deferredPrompt;
}

export function isPwaPromptSuppressed() {
  if (typeof window === 'undefined') return true;
  try {
    if (window.localStorage.getItem(PWA_NEVER_KEY) === '1') return true;
    const until = Number(window.localStorage.getItem(PWA_DISMISS_KEY) || 0) || 0;
    if (until && Date.now() < until) return true;
  } catch {
    return false;
  }
  return false;
}

export function dismissPwaPrompt({ never = false } = {}) {
  try {
    if (never) {
      window.localStorage.setItem(PWA_NEVER_KEY, '1');
      window.localStorage.removeItem(PWA_DISMISS_KEY);
    } else {
      window.localStorage.setItem(PWA_DISMISS_KEY, String(Date.now() + PWA_DISMISS_MS));
    }
  } catch {
    /* ignore */
  }
}

export function clearPwaPromptSuppression() {
  try {
    window.localStorage.removeItem(PWA_NEVER_KEY);
    window.localStorage.removeItem(PWA_DISMISS_KEY);
  } catch {
    /* ignore */
  }
}

export function openPwaInstallPrompt() {
  if (typeof window === 'undefined') return;
  clearPwaPromptSuppression();
  installPwaCapture();
  window.dispatchEvent(new CustomEvent('aurax:open-pwa-install'));
}

export function shouldOfferPwaInstall() {
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (isStandalonePwa()) return false;
  if (isPwaPromptSuppressed()) return false;
  return true;
}

export function installPwaCapture() {
  if (captureInstalled || typeof window === 'undefined') return;
  captureInstalled = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    try {
      event.preventDefault();
    } catch {
      /* ignore */
    }
    deferredPrompt = event;
    window.dispatchEvent(new CustomEvent('aurax:pwa-installable'));
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try {
      window.localStorage.setItem(PWA_NEVER_KEY, '1');
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('aurax:pwa-installed'));
  });
}

export async function promptNativeInstall() {
  const promptEvent = deferredPrompt;
  if (!promptEvent || typeof promptEvent.prompt !== 'function') {
    return 'unavailable';
  }
  deferredPrompt = null;
  try {
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice?.outcome === 'accepted') {
      try {
        window.localStorage.setItem(PWA_NEVER_KEY, '1');
      } catch {
        /* ignore */
      }
      return 'accepted';
    }
    return 'dismissed';
  } catch {
    return 'unavailable';
  }
}

function absoluteIcon(url, origin) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) return `${origin}${raw}`;
  return `${origin}/${raw}`;
}

/** Ícone efectivo do app (custom do Office ou padrão do código-fonte). */
export function getPwaIconUrl() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (currentPwaIconUrl) return absoluteIcon(currentPwaIconUrl, origin) || `${origin}/pwa-icon-192.png?v=dt2910`;
  return `${origin}/pwa-icon-192.png?v=dt2910`;
}

/**
 * Atualiza o manifest e meta tags com o branding do escritório.
 * @param {{ brand_name?: string, theme_color?: string, icon_url?: string, pwa_icon_url?: string }} opts
 */
export function applyPwaBrand({ brand_name, theme_color, icon_url, pwa_icon_url } = {}) {
  if (typeof document === 'undefined') return;

  const brand = String(brand_name || '').trim() || 'Dragon Tech';
  const short = brand.length > 12 ? brand.slice(0, 12) : brand;
  const theme = String(theme_color || '').trim() || '#0D1117';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const requested = absoluteIcon(pwa_icon_url || icon_url, origin);
  // Settings may explicitly select the packaged PWA icon. Keep its dedicated
  // sizes/maskable variant instead of declaring one 512px file as every size.
  const packaged = requested === `${origin}/pwa-icon-512.png?v=dt2910`
    || requested === `${origin}/pwa-icon-192.png?v=dt2910`;
  const custom = packaged ? '' : requested;
  currentPwaIconUrl = custom || '';

  const icon192 = custom || `${origin}/pwa-icon-192.png?v=dt2910`;
  const icon512 = custom || `${origin}/pwa-icon-512.png?v=dt2910`;
  const icons = custom
    ? [
        { src: custom, sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: custom, sizes: '512x512', type: 'image/png', purpose: 'any' },
        { src: custom, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ]
    : [
        { src: icon192, sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: icon512, sizes: '512x512', type: 'image/png', purpose: 'any' },
        { src: `${origin}/pwa-maskable-512.png?v=dt2910`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ];

  const manifest = {
    name: brand,
    short_name: short,
    description: `${brand} — abra como aplicativo no celular ou no computador.`,
    start_url: `${origin}/?source=pwa`,
    scope: `${origin}/`,
    id: `${origin}/`,
    display: 'standalone',
    orientation: 'any',
    background_color: theme,
    theme_color: theme,
    lang: 'pt-BR',
    categories: ['business', 'productivity'],
    icons,
  };

  try {
    if (manifestObjectUrl) URL.revokeObjectURL(manifestObjectUrl);
  } catch {
    /* ignore */
  }
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  manifestObjectUrl = URL.createObjectURL(blob);

  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  link.href = manifestObjectUrl;

  const setMeta = (name, content, attr = 'name') => {
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  setMeta('application-name', brand);
  setMeta('apple-mobile-web-app-title', short);
  setMeta('theme-color', theme);

  // App icons belong to the manifest and Apple touch link. The tab favicon is
  // independent and managed only by applyBrowserBrand, including when cleared.
  const apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (apple) apple.setAttribute('href', icon192);

  try {
    window.dispatchEvent(
      new CustomEvent('aurax:pwa-brand-updated', { detail: { brand_name: brand, icon_url: icon192 } }),
    );
  } catch {
    /* ignore */
  }
}
